import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, Phone, ArrowUpRight, Loader2 } from "lucide-react";
import { COMPANY } from "@/lib/site-data";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INITIAL = { name: "", email: "", company: "", industry: "", message: "" };

const INDUSTRY_OPTIONS = [
  "Information Technology",
  "Water & Sustainability",
  "Financial Services / Fintech",
  "Other",
];

export const Contact = () => {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) return toast.error("Please share your name.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      return toast.error("A valid email is required.");
    if (form.message.trim().length < 10)
      return toast.error("Please add a brief message (10+ characters).");

    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      setForm(INITIAL);
      toast.success("Inquiry received. A partner will reply within one business day.");
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Could not submit. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="bg-slate-950 text-white py-24 md:py-32 relative grain"
    >
      <div className="container-narrow grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="overline text-emerald-400 mb-5">— Contact</div>
          <h2 className="font-serif text-3xl md:text-5xl tracking-tight leading-tight text-white">
            Start a conversation with a partner.
          </h2>
          <p className="mt-6 text-slate-300 leading-relaxed">
            Tell us about the programme on your desk. We reply to every serious
            inquiry within one business day — usually with a written point of
            view, not a brochure.
          </p>

          <div className="mt-10 space-y-6">
            <a
              data-testid="contact-email"
              href={`mailto:${COMPANY.email}`}
              className="flex items-start gap-4 group"
            >
              <Mail size={20} strokeWidth={1.5} className="text-emerald-400 mt-1" />
              <div>
                <div className="overline text-slate-400 mb-1">Email</div>
                <div className="text-white group-hover:text-emerald-300 transition-colors">
                  {COMPANY.email}
                </div>
              </div>
            </a>
            <div className="flex items-start gap-4">
              <MapPin size={20} strokeWidth={1.5} className="text-emerald-400 mt-1" />
              <div>
                <div className="overline text-slate-400 mb-1">Headquarters</div>
                <div className="text-white">{COMPANY.hqAddress}</div>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Phone size={20} strokeWidth={1.5} className="text-emerald-400 mt-1" />
              <div>
                <div className="overline text-slate-400 mb-1">Office hours</div>
                <div className="text-white">Mon–Fri · 09:00 – 18:00 CET</div>
              </div>
            </div>
          </div>

          <div className="mt-12 border-t border-white/10 pt-6 text-xs text-slate-400 leading-relaxed">
            <strong className="text-white">{COMPANY.legalName}</strong> ·
            REGON / NIP {COMPANY.regon} · Registered in Poland.
            All inquiries are handled in confidence under our standard NDA on request.
          </div>
        </div>

        <div className="md:col-span-7">
          <form
            data-testid="contact-form"
            onSubmit={submit}
            className="bg-white text-slate-950 p-6 sm:p-8 md:p-12 border border-white/10"
          >
            {submitted ? (
              <div
                data-testid="contact-success"
                className="py-10 flex flex-col items-start gap-5"
              >
                <div className="overline text-emerald-800">— Received</div>
                <h3 className="font-serif text-3xl tracking-tight leading-tight">
                  Thank you. Your inquiry is with our partners.
                </h3>
                <p className="text-slate-600">
                  We confirm receipt within one business day. Anything urgent
                  can be sent directly to{" "}
                  <a className="underline" href={`mailto:${COMPANY.email}`}>
                    {COMPANY.email}
                  </a>
                  .
                </p>
                <button
                  data-testid="contact-reset"
                  type="button"
                  onClick={() => setSubmitted(false)}
                  className="mt-3 border border-slate-950 px-5 py-2.5 text-sm hover:bg-slate-950 hover:text-white transition-colors"
                >
                  Submit another inquiry
                </button>
              </div>
            ) : (
              <>
                <div className="overline text-emerald-800 mb-6">— New inquiry</div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-xs text-slate-600 mb-1 block">
                      Full name *
                    </label>
                    <input
                      data-testid="contact-name"
                      type="text"
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Anna Kowalska"
                      required
                      className="bg-transparent border-b border-slate-300 focus:border-emerald-800 outline-none py-3 w-full transition-colors placeholder:text-slate-400"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-600 mb-1 block">
                      Work email *
                    </label>
                    <input
                      data-testid="contact-email-input"
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      placeholder="anna@company.eu"
                      required
                      className="bg-transparent border-b border-slate-300 focus:border-emerald-800 outline-none py-3 w-full transition-colors placeholder:text-slate-400"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-600 mb-1 block">
                      Company
                    </label>
                    <input
                      data-testid="contact-company"
                      type="text"
                      value={form.company}
                      onChange={update("company")}
                      placeholder="Acme Holdings"
                      className="bg-transparent border-b border-slate-300 focus:border-emerald-800 outline-none py-3 w-full transition-colors placeholder:text-slate-400"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-600 mb-1 block">
                      Industry
                    </label>
                    <select
                      data-testid="contact-industry"
                      value={form.industry}
                      onChange={update("industry")}
                      className="bg-transparent border-b border-slate-300 focus:border-emerald-800 outline-none py-3 w-full transition-colors text-slate-950"
                    >
                      <option value="">Select…</option>
                      {INDUSTRY_OPTIONS.map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mt-6">
                  <label className="text-xs text-slate-600 mb-1 block">
                    How can we help? *
                  </label>
                  <textarea
                    data-testid="contact-message"
                    value={form.message}
                    onChange={update("message")}
                    placeholder="A few lines on the programme, timeline and decision you face."
                    required
                    rows={5}
                    className="bg-transparent border-b border-slate-300 focus:border-emerald-800 outline-none py-3 w-full transition-colors placeholder:text-slate-400 resize-none"
                  />
                </div>

                <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <p className="text-xs text-slate-500 max-w-sm leading-relaxed">
                    By submitting you agree to be contacted regarding your
                    inquiry. We do not share details with third parties.
                  </p>
                  <button
                    data-testid="contact-submit"
                    type="submit"
                    disabled={submitting}
                    className="bg-slate-950 hover:bg-emerald-800 transition-colors text-white px-7 py-4 inline-flex items-center gap-3 text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? (
                      <>
                        <Loader2 size={16} className="animate-spin" /> Sending…
                      </>
                    ) : (
                      <>
                        Send inquiry
                        <ArrowUpRight size={16} strokeWidth={1.5} />
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
